import React, { useState, useEffect } from 'react';
import styles from './UserManagement.module.css';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [studies, setStudies] = useState([]);
    const [sites, setSites] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userHistory, setUserHistory] = useState([]);
    const [loading, setLoading] = useState(false);

    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        type: 'info',
        confirmText: 'OK',
        cancelText: 'Cancel',
        onConfirm: null,
        onCancel: null
    });

    const [formData, setFormData] = useState({
        full_name: '',
        email_address: '',
        contact_number: '',
        role_id: '',
        study_id: '',
        site_id: '',
        status: 'Active'
    });

    const getToken = () => localStorage.getItem('token');
    const getCurrentUser = () => {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    };

    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            if (isMounted) {
                await Promise.allSettled([fetchUsers(), fetchRoles(), fetchStudies(), fetchSites()]);
            }
        };
        loadData();
        return () => { isMounted = false; };
    }, [searchTerm, currentPage, rowsPerPage]);

    const fetchRoles = async () => {
        try {
            const token = getToken();
            const response = await fetch('https://hms-render.onrender.com/api/roles/dropdown/list', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                console.log('  Roles:', data.data.length);
                setRoles(data.data);
            }
        } catch (error) {
            console.error(' Error fetching roles:', error);
        }
    };

    const fetchStudies = async () => {
        try {
            const token = getToken();
            const response = await fetch('https://hms-render.onrender.com/api/studies/dropdown/list', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                console.log(' Studies:', data.data?.length || 0);
                setStudies(data.data || []);
            }
        } catch (error) {
            console.error(' Error fetching studies:', error);
            setStudies([]);
        }
    };

    const fetchSites = async () => {
        try {
            const token = getToken();
            const response = await fetch('https://hms-render.onrender.com/api/sites/dropdown/list', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                console.log(' Sites:', data.data?.length || 0);
                setSites(data.data || []);
            }
        } catch (error) {
            console.error(' Error fetching sites:', error);
            setSites([]);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const token = getToken();
            const response = await fetch('https://hms-render.onrender.com/api/users/list', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    page: currentPage,
                    limit: rowsPerPage,
                    search: searchTerm
                })
            });
            const data = await response.json();
            if (data.success) {
                console.log(' Users:', data.data.length);
                setUsers(data.data);
                setTotalPages(data.pagination.totalPages);
                setTotalRecords(data.pagination.totalRecords);
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to fetch users',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error(' Error:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'Failed to fetch users',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = () => {
        setFormData({ full_name: '', email_address: '', contact_number: '', role_id: '', study_id: '', site_id: '', status: 'Active' });
        setSelectedUser(null);
        setShowCreateModal(true);
    };

    const handleEdit = (user) => {
        setSelectedUser(user);
        setFormData({
            full_name: user.full_name,
            email_address: user.email_address,
            contact_number: user.contact_number || '',
            role_id: user.role_id || '',
            study_id: user.study_id || '',
            site_id: user.site_id || '',
            status: user.status
        });
        setShowEditModal(true);
    };

    const handleHistory = async (user) => {
        setSelectedUser(user);
        setShowHistoryModal(true);
        try {
            const token = getToken();
            const response = await fetch(`https://hms-render.onrender.com/api/users/${user.user_id}/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) setUserHistory(data.data);
        } catch (error) {
            console.error('Error fetching history:', error);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = getToken();
            const currentUser = getCurrentUser();
            const response = await fetch('https://hms-render.onrender.com/api/users/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    full_name: formData.full_name,
                    email_address: formData.email_address,
                    contact_number: formData.contact_number,
                    role_id: parseInt(formData.role_id),
                    study_id: formData.study_id ? parseInt(formData.study_id) : null,
                    site_id: formData.site_id ? parseInt(formData.site_id) : null,
                    status: formData.status,
                    created_by: currentUser?.userId || currentUser?.user_id
                })
            });
            const data = await response.json();
            if (data.success) {
                setShowCreateModal(false);
                fetchUsers();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'User created successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to create user',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('Error:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'An error occurred while creating user',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = getToken();
            const currentUser = getCurrentUser();
            const response = await fetch(`https://hms-render.onrender.com/api/users/${selectedUser.user_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    full_name: formData.full_name,
                    email_address: formData.email_address,
                    contact_number: formData.contact_number,
                    role_id: parseInt(formData.role_id),
                    study_id: formData.study_id ? parseInt(formData.study_id) : null,
                    site_id: formData.site_id ? parseInt(formData.site_id) : null,
                    status: formData.status,
                    updated_by: currentUser?.userId || currentUser?.user_id
                })
            });
            const data = await response.json();
            if (data.success) {
                setShowEditModal(false);
                fetchUsers();
                setConfirmModal({
                    show: true,
                    title: 'Success!',
                    message: 'User updated successfully!',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to update user',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error('Error:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'An error occurred while updating user',
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const handleDelete = (user) => {
        setConfirmModal({
            show: true,
            title: 'Delete User',
            message: `Are you sure you want to permanently delete ${user.full_name}? This action cannot be undone and will remove all user data.`,
            type: 'delete',
            confirmText: 'Delete Permanently',
            cancelText: 'Cancel',
            onConfirm: () => {
                setConfirmModal({ ...confirmModal, show: false });
                performDelete(user);
            },
            onCancel: () => setConfirmModal({ ...confirmModal, show: false })
        });
    };

    const performDelete = async (user) => {
        try {
            const token = getToken();
            const currentUser = getCurrentUser();

            console.log(' Deleting user:', user);
            console.log(' Current user:', currentUser);

            const response = await fetch(`https://hms-render.onrender.com/api/users/${user.user_id}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ deleted_by: currentUser?.userId || currentUser?.user_id })
            });

            console.log('📡 Response status:', response.status);
            const data = await response.json();
            console.log('📦 Response data:', data);

            if (data.success) {
                console.log(' User deleted successfully');
                fetchUsers();
                setConfirmModal({
                    show: true,
                    title: 'Deleted!',
                    message: 'User has been permanently deleted.',
                    type: 'success',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            } else {
                console.log(' Delete failed:', data.message);
                setConfirmModal({
                    show: true,
                    title: 'Error',
                    message: data.message || 'Failed to delete user',
                    type: 'error',
                    onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
                });
            }
        } catch (error) {
            console.error(' Delete error:', error);
            setConfirmModal({
                show: true,
                title: 'Error',
                message: 'An error occurred: ' + error.message,
                type: 'error',
                onConfirm: () => setConfirmModal({ ...confirmModal, show: false })
            });
        }
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>User Management</h1>
                <button className={styles.createButton} onClick={handleCreateUser}>
                    <span className={styles.buttonIcon}>+</span>
                    Create User
                </button>
            </div>

            <div className={styles.searchBar}>
                <div className={styles.searchWrapper}>
                    <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search by name, email, or contact..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
                <div className={styles.actionButtons}>
                    <button className={styles.actionButton}><span className={styles.actionIcon}>⚙</span>Customize</button>
                    <button className={styles.actionButton}>Import</button>
                    <button className={styles.actionButton}><span className={styles.actionIcon}>↓</span>Export</button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', color: '#666' }}>Loading users...</div>
                ) : users.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No users found</div>
                ) : (
                    <table className={styles.table}>
                        <thead className={styles.tableHead}>
                            <tr>
                                <th>Full Name</th>
                                <th>Contact Number</th>
                                <th>Role</th>
                                <th>Study</th>
                                <th>Site</th>
                                <th>Status</th>
                                <th className={styles.actionsColumn}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className={styles.tableBody}>
                            {users.map((user) => (
                                <tr key={user.user_id} className={styles.tableRow}>
                                    <td>
                                        <div className={styles.userCell}>
                                            <div className={styles.avatar}>{getInitials(user.full_name)}</div>
                                            <div className={styles.userInfo}>
                                                <div className={styles.userName}>{user.full_name}</div>
                                                <div className={styles.userEmail}>{user.email_address}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{user.contact_number || '-'}</td>
                                    <td><span className={styles.roleBadge}>{user.role_name || '-'}</span></td>
                                    <td>{user.study_title || '-'}</td>
                                    <td>{user.site_name || '-'}</td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${user.status === 'Active' ? styles.statusActive : styles.statusInactive}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className={styles.actionIcons}>
                                            <button className={styles.iconButton} onClick={() => handleEdit(user)} title="Edit">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </button>
                                            <button className={styles.iconButton} onClick={() => handleDelete(user)} title="Delete">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6" />
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                                </svg>
                                            </button>
                                            <button className={styles.iconButton} onClick={() => handleHistory(user)} title="History">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <polyline points="12 6 12 12 16 14" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className={styles.pagination}>
                <div className={styles.rowsPerPage}>
                    <span>Rows per page</span>
                    <select className={styles.rowsSelect} value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                <div className={styles.pageInfo}>Page {currentPage} of {totalPages || 1} ({totalRecords} total)</div>
                <div className={styles.pageControls}>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>««</button>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>‹</button>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>›</button>
                    <button className={styles.pageButton} onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»»</button>
                </div>
            </div>

            {showCreateModal && (
                <div className={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Create New User</h2>
                            <button className={styles.modalClose} onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Full Name *</label>
                                    <input type="text" className={styles.input} required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder="Enter full name" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Email Address *</label>
                                    <input type="email" className={styles.input} required value={formData.email_address} onChange={(e) => setFormData({ ...formData, email_address: e.target.value })} placeholder="user@example.com" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Contact Number *</label>
                                    <input type="text" className={styles.input} required value={formData.contact_number} onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })} placeholder="+1234567890" />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Role *</label>
                                    <select className={styles.input} required value={formData.role_id} onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}>
                                        <option value="">Select Role</option>
                                        {roles.map((role) => (<option key={role.role_id} value={role.role_id}>{role.role_name}</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Study *</label>
                                    <select className={styles.input} required value={formData.study_id} onChange={(e) => setFormData({ ...formData, study_id: e.target.value })}>
                                        <option value="">Select Study</option>
                                        {studies.map((study) => (<option key={study.study_id} value={study.study_id}>{study.study_title} ({study.study_number})</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Site *</label>
                                    <select className={styles.input} required value={formData.site_id} onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}>
                                        <option value="">Select Site</option>
                                        {sites.map((site) => (<option key={site.site_id} value={site.site_id}>{site.site_name} ({site.site_code})</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Status *</label>
                                    <select className={styles.input} required value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.cancelButton} onClick={() => setShowCreateModal(false)}>Cancel</button>
                                <button type="submit" className={styles.submitButton}>Create User</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showEditModal && (
                <div className={styles.modalOverlay} onClick={() => setShowEditModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>Edit User</h2>
                            <button className={styles.modalClose} onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleEditSubmit}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Full Name *</label>
                                    <input type="text" className={styles.input} required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Email Address *</label>
                                    <input type="email" className={styles.input} required value={formData.email_address} onChange={(e) => setFormData({ ...formData, email_address: e.target.value })} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Contact Number *</label>
                                    <input type="text" className={styles.input} required value={formData.contact_number} onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Role *</label>
                                    <select className={styles.input} required value={formData.role_id} onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}>
                                        <option value="">Select Role</option>
                                        {roles.map((role) => (<option key={role.role_id} value={role.role_id}>{role.role_name}</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Study *</label>
                                    <select className={styles.input} required value={formData.study_id} onChange={(e) => setFormData({ ...formData, study_id: e.target.value })}>
                                        <option value="">Select Study</option>
                                        {studies.map((study) => (<option key={study.study_id} value={study.study_id}>{study.study_title} ({study.study_number})</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Site *</label>
                                    <select className={styles.input} required value={formData.site_id} onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}>
                                        <option value="">Select Site</option>
                                        {sites.map((site) => (<option key={site.site_id} value={site.site_id}>{site.site_name} ({site.site_code})</option>))}
                                    </select>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Status *</label>
                                    <select className={styles.input} required value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className={styles.modalFooter}>
                                <button type="button" className={styles.cancelButton} onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="submit" className={styles.submitButton}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showHistoryModal && (
                <div className={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2 className={styles.modalTitle}>User Activity History</h2>
                            <button className={styles.modalClose} onClick={() => setShowHistoryModal(false)}>×</button>
                        </div>
                        <div className={styles.modalBody}>
                            <p><strong>User:</strong> {selectedUser?.full_name}</p>
                            <div className={styles.historyContent}>
                                {userHistory.length === 0 ? (
                                    <p className={styles.noHistory}>No history available</p>
                                ) : (
                                    <div className={styles.historyList}>
                                        {userHistory.map((item) => (
                                            <div key={item.audit_id} className={styles.historyItem}>
                                                <div className={styles.historyHeader}>
                                                    <span className={styles.historyAction}>{item.action_type}</span>
                                                    <span className={styles.historyDate}>{new Date(item.timestamp).toLocaleString()}</span>
                                                </div>
                                                <div className={styles.historyDetails}>
                                                    <p><strong>Module:</strong> {item.module_name}</p>
                                                    <p><strong>Performed by:</strong> {item.performed_by || 'System'}</p>
                                                    <p><strong>IP Address:</strong> {item.ip_address}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setShowHistoryModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                show={confirmModal.show}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.confirmText}
                cancelText={confirmModal.cancelText}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ ...confirmModal, show: false }))}
            />
        </div>
    );
};

export default UserManagement;